import { Button, ButtonCus, ButtonCusWithIcon } from '@/components/common/Button';
import Combobox from '@/components/common/Combobox';
import { Form, FormControl, FormField, FormItem } from '@/components/common/Form';
import ApproveBikeStatus from '@/components/general/ApproveBikeStatus';
import Table from '@/components/table/Table';
import { DEFAULT_SELECT_PAGE_SIZES, DISPPLAY_MOMENT_YMD_HM, ekDomain, ROUTES } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { ApproveBike } from '@/types/approve';
import { APPROVE_BIKE_STATUS, GROUP_ROLE } from '@/types/enums';
import { yupResolver } from '@hookform/resolvers/yup';
import { ColumnDef, TableState, Updater } from '@tanstack/react-table';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { isPathEk } from '@/utils/routes';
import { useLocation, useNavigate } from 'react-router-dom';
import BikeNoteDetail from '../../component/BikeNoteDetail';
import NoteForm from '../../component/NoteForm';
import TableActionButtons from '@/components/common/TableActionButtons';
import { formatDateTime } from '@/utils/datetime';
import DeletedLabel from '@/components/general/DeletedLabel';
import { isDeleted } from '@/utils/utils';

export default observer(function TabItem({ approveStatus }: { approveStatus: number }) {
    const showAction = [APPROVE_BIKE_STATUS.ResendApprove, APPROVE_BIKE_STATUS.WaittingApprove].includes(approveStatus);
    // hooks
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { pathname } = useLocation();

    // store
    const {
        // accountStore: { accounts, getAccounts, paging: accountPaging },
        approveStore: { approveBikeList, paging, totalRecord, getAllBikeApprove, clean },
        generalStore: { usersByType, getUsersByType },
        modalStore: { showModal, hideAllModals }
    } = useStore();

    // state
    const [rowSelected, setRowSelected] = useState({});
    const [searchParams, setSearchParams] = useState({ approveStatus });

    // lifecycle
    useEffect(() => {
        getAllBikeApprove({ approveStatus });
        getUsersByType({ userRole: GROUP_ROLE.OWNER });
        // getAccounts({ isOwner: true }, { ...accountPaging, pagination: { ...accountPaging.pagination, pageIndex: -2 } });

        return () => {
            clean();
        };
    }, []);

    // Validation schema
    const validateSchema = yup.object().shape({
        bikeOwnerId: yup.number(),
        approveStatus: yup.number(),
        bikeLicense: yup.string()
    });

    type FormData = yup.InferType<typeof validateSchema>;

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onSubmit',
        defaultValues: {
            bikeOwnerId: undefined,
            approveStatus: undefined,
            bikeLicense: ''
        }
    });

    // functions
    const onSearch = (searchParams?: any) => {
        getAllBikeApprove(searchParams, { ...paging, pagination: { ...paging.pagination, pageIndex: 0 } });
        setSearchParams(searchParams);
    };

    const onStateChange = (stateUpdater: Updater<TableState>) => {
        const newPaging = stateUpdater instanceof Function ? stateUpdater(paging) : stateUpdater;
        getAllBikeApprove({ ...searchParams, approveStatus }, newPaging);
    };

    const reloadData = () => {
        hideAllModals();
        getAllBikeApprove({ ...searchParams, approveStatus }, paging);
        setRowSelected({});
    };

    const handleClickNote = (id: number) => {
        showModal({
            id: 'bike-note',
            size: '2xl',
            title: t('words_title.approve_rejeject_resion'),
            titleClassName: 'text-center w-full',
            footerClassName: 'p-0',
            content: (
                <BikeNoteDetail id={id} />
            )
        });
    };

    const handleClickApproveReject = async (ids: number[], isApprove: boolean) => {
        showModal({
            id: 'approve-reject-note',
            size: 'xl',
            title: t('words_title.approve_rejeject_resion'),
            titleClassName: 'text-center w-full',
            content: (
                <NoteForm ids={ids} isApprove={isApprove} reloadData={reloadData} isBike={true} />
            ),
            footerClassName: 'hidden'
        });
    };

    const handleClickBikeDetail = (id: number) => {
        navigate(isPathEk(pathname) ?
            `/${ekDomain}/${ROUTES.bikeManagement.hrefEK}/${ROUTES.bikeEdit.hrefEK}/${id}` :
            `${ROUTES.bikeManagement.href}/${ROUTES.bikeEdit.href}/${id}`, { state: { previousUrl: pathname } });
    };

    // columns
    const columns: ColumnDef<ApproveBike>[] = [
        {
            id: 'actions',
            accessorKey: 'actions',
            header: '',
            size: 50,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <TableActionButtons
                    onView={() => handleClickBikeDetail(original.id)}
                />
            )
        },
        {
            accessorKey: 'bikeImage',
            header: '',
            size: 120,
            enableSorting: false,
            cell: ({ row: { original } }) => {
                const imgUrl = original?.vehicleImageRight || original?.vehicleImageLeft || original?.vehicleImageBefore || original?.vehicleImageAfter;
                return (
                    <div>
                        {imgUrl &&
                            <img src={imgUrl ?? ''} alt={t('field.bike_image')} loading='lazy' className='max-w-[70px] h-[50px] object-contain' />}
                    </div>
                );
            }
        },
        {
            accessorKey: 'approveBikeHistoriesDate',
            header: t('field.send_date'),
            size: 200,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='whitespace-nowrap'>
                    {formatDateTime(original?.approveBikeHistoriesDate, DISPPLAY_MOMENT_YMD_HM)}
                </div>
            )
        },
        {
            accessorKey: 'bikeOwner.name',
            header: t('field.owner'),
            size: 300,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <p className='line-clamp-2 break-all min-w-[90px]'>{original?.bikeOwner?.name}</p>
            )
        },
        {
            accessorKey: 'bikeOwner.code',
            header: t('field.user_code'),
            size: 300,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <p className='line-clamp-2 break-all min-w-[90px]'>{original?.bikeOwner?.code}</p>
            )
        },
        {
            accessorKey: 'bikeModel',
            header: t('field.model_name'),
            size: 200,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='break-all min-w-[180px]'>
                    {/* <p className='break-all min-w-[120px] line-clamp-2'>
                        {original.bikeModel || '--'}
                    </p> */}
                    <div className='flex flex-wrap justify-start items-center gap-1.5'>
                        {isDeleted(Number(original?.deleteStatus)) && (
                            <div className='inline-block w-fit h-fit'>
                                <DeletedLabel />
                            </div>
                        )}
                        <span className='line-clamp-2 flex-1 min-w-[120px]'>{original.bikeModel ?? ''}</span>
                    </div>
                </div>
            )
        },
        {
            accessorKey: 'approveStatus',
            header: t('field.status'),
            size: 150,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <ApproveBikeStatus status={Number(approveStatus)} />
            )
        },
        ...!showAction ?
            [{
                accessorKey: 'note',
                header: t('field.approve_rejeject_resion'),
                size: 200,
                enableSorting: false,
                cell: ({ row: { original } }) => (
                    <>
                        {
                            original.isExistedNote && (
                                <Button
                                    onClick={() => handleClickNote(+original.id)}
                                    variant='link'
                                    className='text-link-1'
                                >
                                    {t('button.view_resion')}
                                </Button>
                            )
                        }
                    </>
                )
            }] :
            [],
        ...showAction ?
            [{
                id: 'approve/reject',
                accessorKey: 'approve/reject',
                header: '',
                size: 180,
                enableSorting: false,
                cell: ({ row: { original } }) => (
                    <div className='flex gap-2'>
                        {[APPROVE_BIKE_STATUS.WaittingApprove, APPROVE_BIKE_STATUS.ResendApprove].includes(Number(original.approveStatus)) && (
                            <ButtonCus
                                onClick={() => handleClickApproveReject([+original.id], true)}
                                variant='green'
                                size='sm'
                                className='w-28'
                                disabled={isDeleted(Number(original?.deleteStatus))}
                            >
                                {t('button.approve')}
                            </ButtonCus>
                        )}
                        {(Number(original.approveStatus) === APPROVE_BIKE_STATUS.WaittingApprove || Number(original.approveStatus) === APPROVE_BIKE_STATUS.ResendApprove) && (
                            <ButtonCus
                                onClick={() => handleClickApproveReject([+original.id], false)}
                                variant='default'
                                size='sm'
                                className='w-28'
                                disabled={isDeleted(Number(original?.deleteStatus))}
                            >
                                {t('button.reject')}
                            </ButtonCus>
                        )}
                    </div>
                )
            }] :
            []

    ];

    return (
        <div className='layout-card'>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSearch)}>
                    <div className='flex flex-wrap gap-6'>
                        <FormField
                            control={form.control}
                            name='bikeOwnerId'
                            render={({ field }) => (
                                <FormItem className='w-[220px]'>
                                    <FormControl>
                                        <Combobox
                                            placeholder={t('field.owner')}
                                            value={field.value?.toString() ?? ''}
                                            onChange={(item) => {
                                                field.onChange(item?.code);
                                                let searchPr = { ...searchParams, bikeOwnerId: item?.code };
                                                getAllBikeApprove(searchPr, { ...paging, pagination: { ...paging.pagination, pageIndex: 0 } });
                                                setSearchParams(searchPr);
                                            }}
                                            items={
                                                (Array.isArray(usersByType) && usersByType?.length > 0 ?
                                                    [
                                                        ...usersByType
                                                            .filter(item => item.email && item.id)
                                                            .map(item => ({
                                                                label: item.fullname,
                                                                labelSearch: item.fullname + item?.userCode,
                                                                value: item.id.toString(),
                                                                code: item.id
                                                            }))
                                                    ] :
                                                    []
                                                )
                                            }
                                        />
                                    </FormControl>
                                </FormItem>
                            )}
                        />
                    </div>
                </form>
            </Form>
            <div className='mt-6'>
                <Table
                    columns={columns}
                    data={approveBikeList}
                    state={{
                        ...paging,
                        rowSelection: rowSelected
                    }}
                    rowCount={totalRecord}
                    onStateChange={onStateChange}
                    selectPageSizes={DEFAULT_SELECT_PAGE_SIZES}
                    getRowId={row => row.id?.toString()}
                    // enableRowSelection={showAction}
                    enableRowSelection={({ original }) => showAction && !isDeleted(Number(original.deleteStatus))}
                    onRowSelectionChange={setRowSelected}
                    renderActions={(
                        showAction && (
                            <div className='flex justify-end gap-4'>
                                <ButtonCusWithIcon
                                    type='button'
                                    variant='outline'
                                    size='md'
                                    onClick={() => handleClickApproveReject(Object.keys(rowSelected).map(Number), false)}
                                    disabled={Object.keys(rowSelected).length < 1}
                                    iconId='#icon-dislike'
                                    widthIcon={19}
                                    heightIcon={16}
                                >
                                    {t('button.reject_all')}
                                </ButtonCusWithIcon>
                                <ButtonCusWithIcon
                                    type='button'
                                    variant='default'
                                    size='md'
                                    onClick={() => handleClickApproveReject(Object.keys(rowSelected).map(Number), true)}
                                    disabled={Object.keys(rowSelected).length < 1}
                                    iconId='#icon-like'
                                    widthIcon={19}
                                    heightIcon={16}
                                >
                                    {t('button.approve_all')}
                                </ButtonCusWithIcon>
                            </div>
                        )
                    )}
                />
            </div>
        </div>
    );
});
